import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, Typography, Chip, CircularProgress, Alert } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import './CustomerMap.css';

// Fix for default marker icon issue in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Create custom markers with health status colors
const createCustomIcon = (color) => {
  const svgIcon = `
    <svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 26 16 26s16-14 16-26C32 7.2 24.8 0 16 0z"
            fill="${color}" stroke="#fff" stroke-width="2"/>
      <circle cx="16" cy="16" r="6" fill="#fff"/>
    </svg>
  `;

  return L.divIcon({
    html: svgIcon,
    className: 'custom-marker',
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -42]
  });
};

// Component to adjust map view when markers change
const MapViewController = ({ customers }) => {
  const map = useMap();

  useEffect(() => {
    if (customers.length > 0) {
      const validCustomers = customers.filter(c => c.lat && c.lng);
      if (validCustomers.length > 0) {
        const bounds = L.latLngBounds(validCustomers.map(c => [c.lat, c.lng]));
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
      }
    }
  }, [customers, map]);

  return null;
};

const CustomerMap = ({ customers, customerHealthStatus, getHealthColor, getHealthLabel }) => {
  const [geocodedCustomers, setGeocodedCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Geocode customer addresses in parallel batches of 5
  useEffect(() => {
    const geocodeCustomers = async () => {
      setLoading(true);
      setError(null);

      const BATCH_SIZE = 5;
      const geocoded = [];

      const geocodeOne = async (customer) => {
        const fullAddress = `${customer.custObject.custAddress}, ${customer.custObject.custCity}, ${customer.custObject.custState} ${customer.custObject.custZip}`;
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=1`,
            { headers: { 'User-Agent': 'PoolRouteMonitor/1.0' } }
          );
          const data = await response.json();
          if (data && data.length > 0) {
            return { ...customer, lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), healthStatus: customerHealthStatus[customer.id] || 'none' };
          }
          // Fallback to city+state with small random offset to avoid pin stacking
          const fallback = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(customer.custObject.custCity + ', ' + customer.custObject.custState)}&limit=1`,
            { headers: { 'User-Agent': 'PoolRouteMonitor/1.0' } }
          );
          const fbData = await fallback.json();
          if (fbData && fbData.length > 0) {
            const offset = () => (Math.random() - 0.5) * 0.02;
            return { ...customer, lat: parseFloat(fbData[0].lat) + offset(), lng: parseFloat(fbData[0].lon) + offset(), healthStatus: customerHealthStatus[customer.id] || 'none' };
          }
        } catch (err) {
          console.error('Geocoding error:', customer.name, err);
        }
        return null;
      };

      // Process in batches — 5 concurrent, 300ms between batches to respect Nominatim
      for (let i = 0; i < customers.length; i += BATCH_SIZE) {
        const batch = customers.slice(i, i + BATCH_SIZE);
        const results = await Promise.all(batch.map(geocodeOne));
        results.forEach(r => r && geocoded.push(r));
        if (i + BATCH_SIZE < customers.length) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      setGeocodedCustomers(geocoded);
      setLoading(false);
    };

    if (customers && customers.length > 0) {
      geocodeCustomers();
    } else {
      setLoading(false);
    }
  }, [customers, customerHealthStatus]);

  // Default center (US center) if no customers
  const defaultCenter = [39.8283, -98.5795];
  const mapCenter = geocodedCustomers.length > 0
    ? [geocodedCustomers[0].lat, geocodedCustomers[0].lng]
    : defaultCenter;

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '600px',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading customer locations...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Geocoding {customers.length} addresses
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (geocodedCustomers.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No customer locations could be mapped. Please check address data.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '600px', width: '100%', borderRadius: 2, overflow: 'hidden', boxShadow: 3 }}>
      <MapContainer
        center={mapCenter}
        zoom={10}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <MapViewController customers={geocodedCustomers} />

        {geocodedCustomers.map((customer) => {
          const healthColor = getHealthColor(customer.healthStatus);
          const icon = createCustomIcon(healthColor);

          return (
            <Marker
              key={customer.id}
              position={[customer.lat, customer.lng]}
              icon={icon}
            >
              <Popup maxWidth={300}>
                <Box sx={{ p: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#003366' }}>
                    {customer.name}
                  </Typography>

                  <Chip
                    label={getHealthLabel(customer.healthStatus)}
                    size="small"
                    sx={{
                      backgroundColor: healthColor,
                      color: 'white',
                      fontWeight: 600,
                      mb: 1.5
                    }}
                  />

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                    <PhoneIcon sx={{ fontSize: 18, color: '#666', mt: 0.3 }} />
                    <Typography variant="body2" color="text.secondary">
                      {customer.contact}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                    <HomeIcon sx={{ fontSize: 18, color: '#666', mt: 0.3 }} />
                    <Typography variant="body2" color="text.secondary">
                      {customer.address}
                    </Typography>
                  </Box>

                  {customer.serviceDay && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <LocationOnIcon sx={{ fontSize: 18, color: '#666', mt: 0.3 }} />
                      <Typography variant="body2" color="text.secondary">
                        Service Day: {customer.serviceDay}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </Box>
  );
};

export default CustomerMap;
